import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { fetchFavoriteIds, toggleFavorite as apiToggle } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FavContext = createContext(null);

export function FavProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());

  const load = useCallback(async () => {
    if (!user) { setIds(new Set()); return; }
    try {
      const r = await fetchFavoriteIds();
      setIds(new Set(r.gig_ids || []));
    } catch { /* not logged in */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (gig_id) => {
    if (!user) return { favorited: false, needsAuth: true };
    // Optimistic
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(gig_id)) next.delete(gig_id); else next.add(gig_id);
      return next;
    });
    try {
      const r = await apiToggle(gig_id);
      // Reconcile
      setIds((prev) => {
        const next = new Set(prev);
        if (r.favorited) next.add(gig_id); else next.delete(gig_id);
        return next;
      });
      return r;
    } catch {
      load();
      toast.error("Couldn't update favorite");
      return { favorited: false, error: true };
    }
  }, [user, load]);

  return (
    <FavContext.Provider value={{ ids, toggle, isFav: (id) => ids.has(id) }}>
      {children}
    </FavContext.Provider>
  );
}

export const useFav = () => useContext(FavContext) || { ids: new Set(), toggle: () => {}, isFav: () => false };
